'use client'

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import Image from 'next/image'
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit3,
  Trash2,
  ExternalLink,
  Users,
  MessageCircle,
  Flame,
  Loader2,
  X,
  Mail,
  UserPlus,
  Check,
  AlertCircle,
  Clock,
  Settings,
  UserMinus,
  Shield,
} from 'lucide-react'
import { formatLifeSpan } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface Memorial {
  id: string
  slug: string
  firstName: string | null
  lastName: string | null
  birthName: string | null
  dateOfBirth: string | null
  dateOfDeath: string | null
  city: string | null
  imagePath: string | null
  publishToSite: boolean
  _count: {
    condolenceComments: number
    candles: number
    memorialAccess: number
  }
}

interface CreateMemorialModalProps {
  onClose: () => void
  onSuccess: () => void
}

function CreateMemorialModal({ onClose, onSuccess }: CreateMemorialModalProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [birthName, setBirthName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [dateOfDeath, setDateOfDeath] = useState('')
  const [city, setCity] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch('/api/admin/memorials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          birthName: birthName || undefined,
          dateOfBirth: dateOfBirth || undefined,
          dateOfDeath: dateOfDeath || undefined,
          city: city || undefined,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Fehler beim Erstellen der Gedenkseite')
        return
      }

      onSuccess()
    } catch {
      setError('Ein unerwarteter Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-memorial-primary/10 flex items-center justify-center">
              <Plus className="w-5 h-5 text-memorial-primary" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Neue Gedenkseite</h2>
              <p className="text-sm text-gray-500">Grunddaten erfassen</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vorname *
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                placeholder="Max"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nachname *
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                placeholder="Mustermann"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
          </div>

          {/* Birth name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Geburtsname (optional)
            </label>
            <input
              type="text"
              value={birthName}
              onChange={(e) => setBirthName(e.target.value)}
              placeholder="z.B. geb. Schmidt"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Geburtsdatum
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sterbedatum
              </label>
              <input
                type="date"
                value={dateOfDeath}
                onChange={(e) => setDateOfDeath(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ort
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="z.B. München"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading || !firstName || !lastName}
              className="flex-1 py-3 px-4 bg-memorial-primary text-white rounded-lg font-medium hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Erstellt...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Erstellen
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

interface InviteModalProps {
  memorial: Memorial
  onClose: () => void
  onSuccess: () => void
}

function InviteModal({ memorial, onClose, onSuccess }: InviteModalProps) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [relationship, setRelationship] = useState('')
  const [role, setRole] = useState<'OWNER' | 'EDITOR' | 'VIEWER'>('EDITOR')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          displayName: displayName || undefined,
          relationship: relationship || undefined,
          role,
          canModerateCondolences: role !== 'VIEWER',
          canUploadPhotos: role !== 'VIEWER',
          canEditDetails: role === 'OWNER',
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Fehler beim Senden der Einladung')
        return
      }

      setSuccess(true)
      if (data.inviteUrl) {
        setInviteUrl(data.inviteUrl)
      }

      setTimeout(() => {
        onSuccess()
      }, 2000)
    } catch {
      setError('Ein unerwarteter Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  const memorialName = `${memorial.firstName} ${memorial.lastName}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-memorial-primary/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-memorial-primary" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Familie einladen</h2>
              <p className="text-sm text-gray-500">{memorialName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Einladung gesendet!</h3>
              <p className="text-gray-600 mb-4">
                Die Einladung wurde an <strong>{email}</strong> gesendet.
              </p>
              {inviteUrl && (
                <div className="p-3 bg-amber-50 rounded-lg text-left text-sm">
                  <p className="text-amber-800 mb-2">
                    <strong>Dev-Modus:</strong> Link für manuelles Teilen:
                  </p>
                  <code className="text-xs break-all text-amber-700">{inviteUrl}</code>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail-Adresse *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="email@beispiel.de"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
                  />
                </div>
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="z.B. Elisabeth Müller"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
                />
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beziehung (optional)
                </label>
                <select
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="Ehepartner">Ehepartner/in</option>
                  <option value="Sohn">Sohn</option>
                  <option value="Tochter">Tochter</option>
                  <option value="Enkel">Enkel/in</option>
                  <option value="Geschwister">Geschwister</option>
                  <option value="Elternteil">Elternteil</option>
                  <option value="Andere Familie">Andere Familie</option>
                  <option value="Freund">Freund/in</option>
                </select>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Berechtigung
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'OWNER', label: 'Inhaber', desc: 'Volle Kontrolle über die Gedenkseite' },
                    { value: 'EDITOR', label: 'Bearbeiter', desc: 'Kann Inhalte bearbeiten und moderieren' },
                    { value: 'VIEWER', label: 'Betrachter', desc: 'Kann nur ansehen' },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors',
                        role === option.value
                          ? 'border-memorial-primary bg-memorial-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={option.value}
                        checked={role === option.value}
                        onChange={(e) => setRole(e.target.value as typeof role)}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{option.label}</p>
                        <p className="text-sm text-gray-500">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !email}
                className="w-full py-3 px-4 bg-memorial-primary text-white rounded-lg font-medium hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Einladung senden
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

interface FamilyMember {
  id: string
  userId: string
  email: string
  displayName: string | null
  relationship: string | null
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  canModerateCondolences: boolean
  canUploadPhotos: boolean
  canEditDetails: boolean
  acceptedAt: string | null
  invitedAt: string
}

interface PendingInvite {
  id: string
  email: string
  displayName: string | null
  relationship: string | null
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  expires: string
  createdAt: string
}

interface UserManagementModalProps {
  memorial: Memorial
  onClose: () => void
  onUpdate: () => void
}

function UserManagementModal({ memorial, onClose, onUpdate }: UserManagementModalProps) {
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingMember, setEditingMember] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}/invite`)
      if (!res.ok) throw new Error('Fehler beim Laden')
      const data = await res.json()
      setMembers(data.members || [])
      setPendingInvites(data.pendingInvites || [])
    } catch (err) {
      setError('Fehler beim Laden der Familienmitglieder')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [memorial.id])

  const handleUpdateRole = async (accessId: string, newRole: string) => {
    setSavingId(accessId)
    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}/invite`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessId,
          role: newRole,
          canModerateCondolences: newRole !== 'VIEWER',
          canUploadPhotos: newRole !== 'VIEWER',
          canEditDetails: newRole === 'OWNER',
        }),
      })
      if (!res.ok) throw new Error('Fehler beim Aktualisieren')
      await fetchData()
      setEditingMember(null)
      onUpdate()
    } catch (err) {
      setError('Fehler beim Aktualisieren der Berechtigung')
      console.error(err)
    } finally {
      setSavingId(null)
    }
  }

  const handleRemoveAccess = async (accessId: string) => {
    if (!confirm('Möchten Sie den Zugang wirklich entfernen?')) return

    setSavingId(accessId)
    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}/invite?accessId=${accessId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Fehler beim Entfernen')
      await fetchData()
      onUpdate()
    } catch (err) {
      setError('Fehler beim Entfernen des Zugangs')
      console.error(err)
    } finally {
      setSavingId(null)
    }
  }

  const handleCancelInvite = async (inviteId: string) => {
    if (!confirm('Möchten Sie die Einladung wirklich zurückziehen?')) return

    setSavingId(inviteId)
    try {
      const res = await fetch(`/api/admin/memorials/${memorial.id}/invite?inviteId=${inviteId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Fehler beim Zurückziehen')
      await fetchData()
    } catch (err) {
      setError('Fehler beim Zurückziehen der Einladung')
      console.error(err)
    } finally {
      setSavingId(null)
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER':
        return 'Inhaber'
      case 'EDITOR':
        return 'Bearbeiter'
      case 'VIEWER':
        return 'Betrachter'
      default:
        return role
    }
  }

  const memorialName = `${memorial.firstName} ${memorial.lastName}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-memorial-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-memorial-primary" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Familienzugang verwalten</h2>
              <p className="text-sm text-gray-500">{memorialName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Active Members */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Aktive Familienmitglieder ({members.length})
                </h3>

                {members.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
                    Noch keine Familienmitglieder hinzugefügt
                  </p>
                ) : (
                  <div className="space-y-2">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {member.displayName || member.email}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {member.email}
                            {member.relationship && ` • ${member.relationship}`}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          {editingMember === member.id ? (
                            <select
                              value={member.role}
                              onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                              disabled={savingId === member.id}
                              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-memorial-primary/20"
                            >
                              <option value="OWNER">Inhaber</option>
                              <option value="EDITOR">Bearbeiter</option>
                              <option value="VIEWER">Betrachter</option>
                            </select>
                          ) : (
                            <span
                              className={cn(
                                'px-2.5 py-1 rounded-full text-xs font-medium',
                                member.role === 'OWNER'
                                  ? 'bg-purple-100 text-purple-700'
                                  : member.role === 'EDITOR'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              )}
                            >
                              {getRoleLabel(member.role)}
                            </span>
                          )}

                          <button
                            onClick={() =>
                              setEditingMember(editingMember === member.id ? null : member.id)
                            }
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                            title="Berechtigung ändern"
                          >
                            <Settings className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleRemoveAccess(member.id)}
                            disabled={savingId === member.id}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Zugang entfernen"
                          >
                            {savingId === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserMinus className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending Invites */}
              {pendingInvites.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Ausstehende Einladungen ({pendingInvites.length})
                  </h3>

                  <div className="space-y-2">
                    {pendingInvites.map((invite) => (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {invite.displayName || invite.email}
                          </p>
                          <p className="text-sm text-gray-500">
                            {invite.email}
                            {invite.relationship && ` • ${invite.relationship}`}
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Läuft ab am{' '}
                            {new Date(invite.expires).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            {getRoleLabel(invite.role)}
                          </span>

                          <button
                            onClick={() => handleCancelInvite(invite.id)}
                            disabled={savingId === invite.id}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Einladung zurückziehen"
                          >
                            {savingId === invite.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AdminMemorialsPage() {
  const [memorials, setMemorials] = useState<Memorial[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const [inviteModal, setInviteModal] = useState<Memorial | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [userManagementModal, setUserManagementModal] = useState<Memorial | null>(null)
  const menuButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  const handleOpenMenu = (memorialId: string) => {
    if (openMenu === memorialId) {
      setOpenMenu(null)
      setMenuPosition(null)
      return
    }

    const button = menuButtonRefs.current[memorialId]
    if (button) {
      const rect = button.getBoundingClientRect()
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 224, // 224px = w-56 (14rem)
      })
    }
    setOpenMenu(memorialId)
  }

  const fetchMemorials = async () => {
    try {
      const res = await fetch('/api/admin/memorials')
      if (res.ok) {
        const data = await res.json()
        setMemorials(data)
      }
    } catch (error) {
      console.error('Error fetching memorials:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMemorials()
  }, [])

  const filteredMemorials = memorials.filter(m => {
    const matchesSearch = !searchQuery ||
      `${m.firstName} ${m.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.city?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'published' && m.publishToSite) ||
      (statusFilter === 'draft' && !m.publishToSite)

    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Gedenkseiten</h1>
          <p className="text-gray-500 mt-1">{memorials.length} Gedenkseiten insgesamt</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neue Gedenkseite erstellen
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name oder Ort suchen..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>

          {/* Status filter */}
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'Alle' },
              { key: 'published', label: 'Veröffentlicht' },
              { key: 'draft', label: 'Entwurf' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setStatusFilter(filter.key as typeof statusFilter)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  statusFilter === filter.key
                    ? 'bg-memorial-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Memorials table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="overflow-x-auto rounded-xl">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-sm font-medium text-gray-600">Person</th>
                <th className="text-left px-5 py-3 text-sm font-medium text-gray-600 hidden md:table-cell">Ort</th>
                <th className="text-left px-5 py-3 text-sm font-medium text-gray-600 hidden lg:table-cell">Lebensdaten</th>
                <th className="text-center px-5 py-3 text-sm font-medium text-gray-600 hidden sm:table-cell">Statistik</th>
                <th className="text-left px-5 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="text-right px-5 py-3 text-sm font-medium text-gray-600">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMemorials.map((memorial) => {
                const fullName = `${memorial.firstName} ${memorial.lastName}`
                const lifeSpan = formatLifeSpan(memorial.dateOfBirth, memorial.dateOfDeath)

                return (
                  <tr key={memorial.id} className="hover:bg-gray-50 transition-colors">
                    {/* Person */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                          {memorial.imagePath ? (
                            <Image
                              src={memorial.imagePath}
                              alt={fullName}
                              fill
                              className="object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm font-medium">
                              {memorial.firstName?.charAt(0)}{memorial.lastName?.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{fullName}</p>
                          {memorial.birthName && (
                            <p className="text-sm text-gray-500">geb. {memorial.birthName}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Location */}
                    <td className="px-5 py-4 hidden md:table-cell">
                      <span className="text-gray-600">{memorial.city || '-'}</span>
                    </td>

                    {/* Life span */}
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className="text-gray-600">{lifeSpan}</span>
                    </td>

                    {/* Stats */}
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1" title="Kondolenzen">
                          <MessageCircle className="w-4 h-4" />
                          {memorial._count.condolenceComments}
                        </span>
                        <span className="flex items-center gap-1" title="Kerzen">
                          <Flame className="w-4 h-4" />
                          {memorial._count.candles}
                        </span>
                        <span className="flex items-center gap-1" title="Familienmitglieder">
                          <Users className="w-4 h-4" />
                          {memorial._count.memorialAccess}
                        </span>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      {memorial.publishToSite ? (
                        <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Veröffentlicht
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          Entwurf
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          href={`/m/${memorial.slug}`}
                          target="_blank"
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Ansehen"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setInviteModal(memorial)}
                          className="p-2 text-gray-500 hover:text-memorial-primary hover:bg-memorial-primary/10 rounded-lg transition-colors"
                          title="Familie einladen"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                        <button
                          ref={(el) => { menuButtonRefs.current[memorial.id] = el }}
                          onClick={() => handleOpenMenu(memorial.id)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredMemorials.length === 0 && (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Keine Gedenkseiten gefunden</h3>
            <p className="text-gray-500">Passen Sie Ihre Suche an oder erstellen Sie eine neue Gedenkseite.</p>
          </div>
        )}
      </div>

      {/* Dropdown Menu Portal */}
      {openMenu && menuPosition && typeof document !== 'undefined' && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => {
              setOpenMenu(null)
              setMenuPosition(null)
            }}
          />
          <div
            className="fixed w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {(() => {
              const memorial = memorials.find((m) => m.id === openMenu)
              if (!memorial) return null
              return (
                <>
                  <Link
                    href={`/admin/memorials/${memorial.id}/edit`}
                    onClick={() => {
                      setOpenMenu(null)
                      setMenuPosition(null)
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <Edit3 className="w-4 h-4 flex-shrink-0" />
                    Bearbeiten
                  </Link>
                  <button
                    onClick={() => {
                      setOpenMenu(null)
                      setMenuPosition(null)
                      setInviteModal(memorial)
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <UserPlus className="w-4 h-4 flex-shrink-0" />
                    Familie einladen
                  </button>
                  <button
                    onClick={() => {
                      setOpenMenu(null)
                      setMenuPosition(null)
                      setUserManagementModal(memorial)
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <Users className="w-4 h-4 flex-shrink-0" />
                    Familienzugang verwalten
                  </button>
                  <hr className="my-1 border-gray-200" />
                  <button className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                    Löschen
                  </button>
                </>
              )
            })()}
          </div>
        </>,
        document.body
      )}

      {/* Create Memorial Modal */}
      {showCreateModal && (
        <CreateMemorialModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            fetchMemorials()
          }}
        />
      )}

      {/* Invite Modal */}
      {inviteModal && (
        <InviteModal
          memorial={inviteModal}
          onClose={() => setInviteModal(null)}
          onSuccess={() => {
            setInviteModal(null)
            fetchMemorials()
          }}
        />
      )}

      {/* User Management Modal */}
      {userManagementModal && (
        <UserManagementModal
          memorial={userManagementModal}
          onClose={() => setUserManagementModal(null)}
          onUpdate={fetchMemorials}
        />
      )}
    </div>
  )
}
